import React, { useEffect, useState, createElement } from 'react';
import { CheckCircle2, XCircle, Clock, Star, FileText, Zap, ShieldCheck, Users, BookOpen, Gift, ChevronDown, AlertCircle, ArrowRight, MessageSquare, Calendar, Layers, Check, Moon, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
// --- Sub-components ---
const CountdownTimer = ({
  dark = false
}: {
  dark?: boolean;
}) => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 27,
    seconds: 35
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          ...prev,
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const bgClass = dark ? 'bg-[#1A0B2E]' : 'bg-[#1A0B2E]';
  return <div className="flex gap-2 font-mono text-xl md:text-4xl font-bold text-white items-start justify-center" data-id="element-1">
      <div className={`${bgClass} px-3 py-2 rounded border border-white/10 shadow-lg`} data-id="element-2">
        {String(timeLeft.hours).padStart(2, '0')}h
      </div>
      <span className="text-white self-center" data-id="element-3">:</span>
      <div className={`${bgClass} px-3 py-2 rounded border border-white/10 shadow-lg`} data-id="element-4">
        {String(timeLeft.minutes).padStart(2, '0')}m
      </div>
      <span className="text-white self-center" data-id="element-5">:</span>
      <div className={`${bgClass} px-3 py-2 rounded border border-white/10 shadow-lg`} data-id="element-6">
        {String(timeLeft.seconds).padStart(2, '0')}s
      </div>
    </div>;
};
const FloatingNotification = () => {
  const [visible, setVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);
  const notifications = [{
    n: 'Sofía M.',
    l: 'Mendoza',
    t: 'hace 6 minutos',
    p: 'el sistema completo'
  }, {
    n: 'Ricardo P.',
    l: 'Buenos Aires',
    t: 'hace 2 minutos',
    p: 'el sistema completo'
  }, {
    n: 'Andrea L.',
    l: 'Santa Fe',
    t: 'hace 15 minutos',
    p: 'el sistema completo'
  }, {
    n: 'Martín G.',
    l: 'Córdoba',
    t: 'hace 8 minutos',
    p: 'el sistema completo'
  }];
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setHasScrolled(true);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (!hasScrolled) return;
    let hideTimeout: NodeJS.Timeout;
    const showNotification = () => {
      setVisible(true);
      hideTimeout = setTimeout(() => {
        setVisible(false);
      }, 4000);
    };
    const initialTimeout = setTimeout(showNotification, 3000);
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % notifications.length);
      showNotification();
    }, 15000);
    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(hideTimeout);
      clearInterval(interval);
    };
  }, [hasScrolled]);
  const current = notifications[currentIndex];
  return <AnimatePresence data-id="element-7">
      {visible && <motion.div initial={{
      opacity: 0,
      y: 50,
      x: -20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} exit={{
      opacity: 0,
      y: 20
    }} className="fixed bottom-6 left-6 z-50 bg-[#1A0B2E] shadow-2xl rounded-lg p-4 border border-[#C9A84C]/30 flex items-center gap-4 max-w-sm" data-id="element-8">
          <div className="w-10 h-10 bg-[#8900F2]/20 rounded-full flex items-center justify-center text-[#8900F2]" data-id="element-9">
            <Sparkles size={20} data-id="element-10" />
          </div>
          <div data-id="element-11">
            <p className="text-[10px] font-semibold text-[#C9A84C] uppercase tracking-widest" data-id="element-12">
              Transformación iniciada
            </p>
            <p className="text-sm text-[#F5F0E8]" data-id="element-13">
              <span className="font-bold" data-id="element-14">{current.n}</span> de {current.l}
              <br data-id="element-15" />
              adquirió{' '}
              <span className="font-bold text-[#C9A84C]" data-id="element-16">{current.p}</span>
            </p>
            <p className="text-xs text-white/40 mt-1" data-id="element-17">{current.t}</p>
          </div>
          <button onClick={() => setVisible(false)} className="text-white/20 hover:text-white/50" data-id="element-18">
            <XCircle size={16} data-id="element-19" />
          </button>
        </motion.div>}
    </AnimatePresence>;
};
const SectionTitle = ({
  children,
  subtitle,
  light = false,
  gold = false
}: {
  children: React.ReactNode;
  subtitle?: string;
  light?: boolean;
  gold?: boolean;
}) => <div className="text-center mb-6 md:mb-16" data-id="element-20">
    <h2 className={`text-3xl md:text-6xl font-bold mb-4 md:mb-6 ${gold ? 'text-[#C9A84C]' : 'text-white'}`} data-id="element-21">
      {children}
    </h2>
    {subtitle && <p className={`text-base md:text-xl font-light leading-relaxed max-w-3xl mx-auto px-4 ${light ? 'text-white/80' : 'text-white/60'}`} data-id="element-22">
        {subtitle}
      </p>}
  </div>;
const CTAButton = ({
  children,
  className = '',
  href = 'https://checkout2.guiadigital360.online/cart/44394437279837:1',
  onClick
}: {
  children: React.ReactNode;
  className?: string;
  href?: string;
  onClick?: () => void;
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: 'Método 33 Ritos',
        value: 14990,
        currency: 'ARS'
      });
    }
    if (onClick) onClick();
  };
  const content = <motion.button animate={{
    scale: [1, 1.03, 1]
  }} transition={{
    duration: 2.5,
    repeat: Infinity,
    ease: 'easeInOut'
  }} className={`${`
        w-full md:w-auto px-6 py-4 md:px-12 md:py-6 rounded-full font-bold text-xl transition-all transform hover:scale-105 active:scale-95 shadow-[0_0_30px_rgba(137,0,242,0.4)]
        bg-[#8900F2] text-white hover:bg-[#7a00d9]
        ${className}
      `} text-[14px] pt-[10px] pb-[10px]`} onClick={handleClick} data-id="element-23">
      {children}
    </motion.button>;
  if (href) {
    const isInternal = href.startsWith('#');
    return <a href={href} target={isInternal ? undefined : '_blank'} rel={isInternal ? undefined : 'noopener noreferrer'} className="w-full md:w-auto block" onClick={e => {
      handleClick(e);
      if (isInternal) {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }} data-id="element-24">
        {content}
      </a>;
  }
  return content;
};
// --- Main Page ---
export function LandingPage() {
  useEffect(() => {
    if (!window.fbq) {
      const f = window as any;
      const b = document;
      const e = 'script';
      const v = 'https://connect.facebook.net/en_US/fbevents.js';
      let n: any, t: any, s: any;
      f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      n = f.fbq;
      if (!f._fbq) f._fbq = n;
      f.fbq.push = f.fbq;
      f.fbq.loaded = !0;
      f.fbq.version = '2.0';
      f.fbq.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      if (s && s.parentNode) s.parentNode.insertBefore(t, s);else b.head.appendChild(t);
    }
    if (window.fbq) {
      window.fbq('init', '1297023029023257');
      window.fbq('track', 'PageView');
    }
  }, []);
  return <div className="min-h-screen bg-[#1A0B2E] text-[#F5F0E8] selection:bg-[#8900F2] selection:text-white" data-id="element-25">
      <FloatingNotification data-id="element-26" />

      {/* Hero Section */}
      <header className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden bg-gradient-to-b from-[#1A0B2E] to-[#2D00F7]/20" data-id="element-27">
        <div className="absolute inset-0 z-0 opacity-30" data-id="element-28">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#8900F2] blur-[150px] rounded-full" data-id="element-29"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[#2D00F7] blur-[150px] rounded-full" data-id="element-30"></div>
        </div>

        <div className="container mx-auto px-4 py-6 md:py-16 relative z-10" data-id="element-31">
          <div className="max-w-5xl mx-auto text-center" data-id="element-32">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-flex items-center gap-2 bg-white/5 border border-[#C9A84C]/30 px-4 py-2 rounded-full text-[#C9A84C] text-xs md:text-sm font-bold mb-4 md:mb-8 backdrop-blur-md uppercase tracking-[0.2em]" data-id="element-33">
              🌑 EL MÉTODO DE LOS 33 RITOS™
            </motion.div>

            <h1 className="md:text-8xl font-bold mb-4 md:mb-8 text-white text-[24px]" data-id="element-34">
              Un sistema completo de{' '}
              <span className="text-[#C9A84C]" data-id="element-35">Psicología Simbólica</span> para
              cerrar ciclos, cortar vínculos y renacer desde adentro.
            </h1>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.2,
            duration: 1
          }} className="relative max-w-4xl mx-auto mb-8 md:mb-16" data-id="element-36">
              <div className="absolute inset-0 bg-[#8900F2] blur-[100px] opacity-20" data-id="element-37"></div>
              <img src="https://cdn.magicpatterns.com/uploads/nHMCVwm4Fede3rMn2MjRDL/metodo_33_ritos_landing_hero_1024.webp" alt="El Método de los 33 Ritos Mockup" className="relative z-10 w-full h-auto object-contain rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-white/5" data-id="element-38" />
            </motion.div>

            <div className="flex flex-col items-center gap-4 md:gap-8 mb-8 md:mb-16" data-id="element-39">
              <CTAButton href="#pricing" className="uppercase tracking-widest px-6 py-4 md:px-16 md:py-7" data-id="element-40">
                → QUIERO EMPEZAR MI TRANSFORMACIÓN HOY
              </CTAButton>

              <div className="flex flex-wrap justify-center gap-3 md:gap-6 text-white/60 font-medium text-xs md:text-sm tracking-widest uppercase" data-id="element-41">
                <div className="flex items-center gap-2" data-id="element-42">
                  <FileText size={16} className="text-[#C9A84C]" data-id="element-43" /> 100% PDF
                </div>
                <div className="flex items-center gap-2" data-id="element-44">
                  <Zap size={16} className="text-[#C9A84C]" data-id="element-45" /> Acceso en 5 min
                </div>
                <div className="flex items-center gap-2" data-id="element-46">
                  <ShieldCheck size={16} className="text-[#C9A84C]" data-id="element-47" /> Garantía
                  7 días
                </div>
              </div>
            </div>

            <div className="inline-block bg-[#1A0B2E]/80 backdrop-blur-xl border border-[#C9A84C]/20 rounded-2xl p-6 md:p-8 shadow-2xl" data-id="element-48">
              <p className="text-[#C9A84C] font-bold uppercase tracking-[0.3em] text-xs md:text-sm mb-4" data-id="element-49">
                ⏰ PRECIO DE LANZAMIENTO CIERRA EN:
              </p>
              <CountdownTimer data-id="element-50" />
            </div>
          </div>
        </div>
      </header>

      {/* Stats Section */}
      <section className="py-10 md:py-24 bg-[#1A0B2E] relative overflow-hidden" data-id="element-51">
        <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-52">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-53"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10" data-id="element-54">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 max-w-5xl mx-auto text-center" data-id="element-55">
            {[{
            label: 'personas en proceso de cierre',
            value: '+1.200'
          }, {
            label: 'tomos y 3 bonos',
            value: '4'
          }, {
            label: 'páginas listas para trabajar',
            value: '270+'
          }, {
            label: 'calificación promedio',
            value: '4.9/5',
            stars: true
          }].map((stat, i) => <div key={i} className="flex flex-col items-center" data-id="element-56">
                <p className="text-4xl md:text-6xl font-bold text-[#C9A84C] mb-3" data-id="element-57">
                  {stat.value}
                </p>
                {stat.stars && <div className="flex justify-center text-[#C9A84C] mb-3" data-id="element-58">
                    {[...Array(5)].map((_, j) => <Star key={j} size={16} fill="currentColor" data-id="element-59" />)}
                  </div>}
                <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-white/50 font-light max-w-[120px] leading-relaxed" data-id="element-60">
                  {stat.label}
                </p>
                {i < 3 && <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 w-px h-12 bg-[#C9A84C]/20" data-id="element-61"></div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Testimonials 1 */}
      <section className="py-12 md:py-32 bg-[#0a0412]" data-id="element-62">
        <div className="container mx-auto px-4" data-id="element-63">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-64">
            {[{
            n: 'María V.',
            l: 'Buenos Aires · Lic. en Psicología',
            q: 'Llevaba 3 años entendiendo por qué no podía soltar esa relación. Con el Tomo II hice el Rito del Hilo Rojo y algo en mí finalmente se movió. No sé cómo explicarlo pero funciona.',
            i: 'MV'
          }, {
            n: 'Carolina A.',
            l: 'Córdoba · Maestra de meditación',
            q: 'El Tomo III me rompió y me reconstruyó en la misma semana. La carta al niño de 7 años fue lo más poderoso que hice en años de terapia.',
            i: 'CA'
          }, {
            n: 'Javier P.',
            l: 'Rosario · Psicólogo clínico',
            q: 'Pensé que era más de lo mismo. Me equivoqué completamente. La diferencia es que acá hacés algo, no solo leés. Y eso cambia todo.',
            i: 'JP'
          }].map((test, i) => <div key={i} className="bg-[#1A0B2E] p-10 rounded-3xl border border-[#C9A84C]/10 shadow-xl relative group hover:border-[#C9A84C]/30 transition-all duration-500" data-id="element-65">
                <div className="flex gap-1 mb-6 text-[#C9A84C]" data-id="element-66">
                  {[...Array(5)].map((_, j) => <Star key={j} size={16} fill="currentColor" data-id="element-67" />)}
                </div>
                <p className="text-[#F5F0E8] italic mb-8 leading-relaxed text-lg font-light" data-id="element-68">
                  "{test.q}"
                </p>
                <div className="flex items-center gap-4" data-id="element-69">
                  <div className="w-12 h-12 bg-[#8900F2]/30 rounded-full flex items-center justify-center font-bold text-[#8900F2] border border-[#8900F2]/50" data-id="element-70">
                    {test.i}
                  </div>
                  <div data-id="element-71">
                    <p className="font-bold text-[#C9A84C] tracking-wide" data-id="element-72">
                      {test.n}
                    </p>
                    <p className="text-xs text-white/40 uppercase tracking-widest" data-id="element-73">
                      {test.l}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Pain Section */}
      <section className="py-12 md:py-32 bg-[#1A0B2E]" data-id="element-74">
        <div className="container mx-auto px-4" data-id="element-75">
          <div className="max-w-4xl mx-auto text-center" data-id="element-76">
            <h2 className="text-3xl md:text-6xl font-bold mb-12 text-[#C9A84C] leading-tight" data-id="element-77">
              ¿Cuántas veces más vas a entender el problema sin poder soltarlo?
            </h2>

            <div className="space-y-8 text-lg md:text-xl font-light leading-[1.8] text-white/80" data-id="element-78">
              <p data-id="element-79">
                Ya hiciste terapia. Ya leíste los libros. Ya analizaste cada
                detalle de lo que pasó.
              </p>
              <p data-id="element-80">
                Y sin embargo ahí está. Ese pensamiento que vuelve a las 3 de la
                mañana. Esa persona que ya no está pero ocupa más espacio en tu
                cabeza que las que sí están. Esa versión de vos que quedó
                atrapada en algo que ya terminó.
              </p>
              <p data-id="element-81">
                No es debilidad. No es falta de esfuerzo. Es que nadie te enseñó
                a hablarle al único que puede soltar de verdad:{' '}
                <span className="text-white font-bold" data-id="element-82">tu inconsciente.</span>
              </p>
              <p data-id="element-83">
                Y el inconsciente no entiende explicaciones.{' '}
                <span className="text-[#8900F2] font-bold" data-id="element-84">
                  Entiende gestos. Entiende símbolos. Entiende rituales con
                  intención.
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Symptoms List */}
      <section className="py-12 md:py-32 bg-gradient-to-b from-[#1A0B2E] to-[#2D0050]/30" data-id="element-85">
        <div className="container mx-auto px-4" data-id="element-86">
          <SectionTitle subtitle="Si te sentís identificado/a con alguno de estos puntos, este sistema fue diseñado para vos." data-id="element-87">
            Seguís cargando algo que ya deberías haber soltado si...
          </SectionTitle>

          <div className="max-w-4xl mx-auto grid gap-4" data-id="element-88">
            {['Entendés racionalmente el problema pero no podés dejar de pensar en él', 'Bloqueaste a esa persona pero igual la buscás o pensás en ella', 'Arrancás bien el día y de la nada algo te dispara el dolor de nuevo', 'Ya fuiste a terapia y aún así el nudo en el estómago sigue ahí', 'Sentís que cargás culpas o mandatos que no elegiste pero no sabés cómo soltar', 'Sabés que algo tuyo sabotea todo justo cuando estás bien', 'Necesitás un cierre que nunca llegó y no sabés cómo dártelo vos mismo', 'Sentís que hay una versión de vos esperando ser liberada'].map((item, i) => <div key={i} className="group flex items-center gap-6 p-6 border-b border-[#C9A84C]/10 hover:bg-white/5 transition-all" data-id="element-89">
                <Moon className="text-[#8900F2] shrink-0 group-hover:rotate-12 transition-transform" size={24} data-id="element-90" />
                <p className="text-lg md:text-xl font-light text-white/90" data-id="element-91">
                  {item}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Break Question */}
      <section className="relative py-16 md:py-48 overflow-hidden" data-id="element-92">
        <div className="absolute inset-0 z-0" data-id="element-93">
          <img src="https://images.unsplash.com/photo-1464802686167-b939a6910659?auto=format&fit=crop&q=80&w=2000" className="w-full h-full object-cover opacity-30" alt="Nebula Background" data-id="element-94" />
          <div className="absolute inset-0 bg-[#1A0B2E]/60 backdrop-blur-[2px]" data-id="element-95"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center" data-id="element-96">
          <motion.h2 initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} className="text-4xl md:text-7xl font-bold text-[#C9A84C] leading-tight max-w-5xl mx-auto drop-shadow-[0_0_20px_rgba(137,0,242,0.5)]" data-id="element-97">
            ¿Y si esta fuera la última vez que atravesás una de estas noches
            completamente sin herramientas?
          </motion.h2>
        </div>
      </section>

      {/* Product Presentation */}
      <section className="py-12 md:py-32 bg-[#1A0B2E] relative overflow-hidden" data-id="element-98">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full pointer-events-none" data-id="element-99">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#8900F2] blur-[200px] opacity-10 rounded-full" data-id="element-100"></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10" data-id="element-101">
          <div className="inline-block bg-[#8900F2] text-white px-8 py-2 rounded-full font-bold text-sm uppercase tracking-[0.3em] mb-8 shadow-[0_0_20px_rgba(137,0,242,0.5)]" data-id="element-102">
            SISTEMA COMPLETO DE TRANSFORMACIÓN
          </div>
          <h2 className="md:text-7xl font-bold mb-6 text-white text-[28px]" data-id="element-103">
            🌑 EL MÉTODO DE LOS 33 RITOS™ <br data-id="element-104" />
            <span className="text-[#C9A84C]" data-id="element-105">
              PACK COMPLETO EDICIONES UMBRAL
            </span>
          </h2>
          <p className="text-xl md:text-3xl text-white/60 mb-16 max-w-4xl mx-auto font-light" data-id="element-106">
            4 Tomos + 3 Bonos · 270+ páginas · El primer sistema de Psicología
            Simbólica completo en español para cerrar, purgar y renacer.
          </p>

          <div className="max-w-5xl mx-auto mb-20 relative group" data-id="element-107">
            <div className="absolute inset-0 bg-[#8900F2] blur-[120px] opacity-20 group-hover:opacity-30 transition-opacity" data-id="element-108"></div>
            <img src="https://cdn.magicpatterns.com/uploads/nHMCVwm4Fede3rMn2MjRDL/metodo_33_ritos_landing_hero_1024.webp" alt="Pack Completo Mockup" className="relative z-10 w-full h-auto rounded-3xl shadow-2xl border border-white/10" data-id="element-109" />
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-110">
            {['Basado en psicología profunda, neurociencia y psicomagia documentada', '100% adaptado a la realidad latinoamericana', 'Cada rito tiene fundamento científico y paso a paso detallado'].map((badge, i) => <div key={i} className="bg-white/5 border border-[#C9A84C]/20 p-6 rounded-2xl backdrop-blur-md flex items-center justify-center text-sm font-bold uppercase tracking-widest text-[#C9A84C]" data-id="element-111">
                ✦ {badge}
              </div>)}
          </div>
        </div>
      </section>

      {/* The 4 Tomos */}
      <section className="py-12 md:py-32 bg-[#0a0412]" data-id="element-112">
        <div className="container mx-auto px-4" data-id="element-113">
          <SectionTitle subtitle="Cada tomo trabaja una capa diferente de tu proceso de transformación." data-id="element-114">
            📚 4 TOMOS MAESTROS
          </SectionTitle>

          <div className="grid gap-12 max-w-5xl mx-auto" data-id="element-115">
            {[{
            n: 'I',
            t: 'EL ORÁCULO DEL INCONSCIENTE',
            d: 'El primer paso: aprender el idioma que tu inconsciente entiende.',
            f: ['El Bucle Mental y por qué ocurre', 'La Ley del Gesto Psicomágico', 'Kit de herramientas sagradas', 'Protocolo de seguridad emocional', '5 Ritos de Iniciación completos']
          }, {
            n: 'II',
            t: 'CORTES VINCULARES',
            d: 'Para soltar lo que el corazón todavía sostiene.',
            f: ['La psicología del vínculo que no cierra', '10 Ritos de corte vincular', 'Ritos para ex-parejas, amigos y familia', 'El rito del perdón a uno mismo', 'La puerta que se cierra para siempre']
          }, {
            n: 'III',
            t: 'PURGA DE SOMBRAS',
            d: 'Para liberar lo que heredaste y lo que reprimiste.',
            f: ['La psicología de la Sombra (Jung)', '12 Ritos de purga profunda', 'Trabajo con el niño interior', 'Deuda del linaje y trauma familiar', 'El pacto con la sombra']
          }, {
            n: 'IV',
            t: 'ARQUITECTURA DEL RENACIMIENTO',
            d: 'Construir la nueva versión de vos con actos conscientes.',
            f: ['La neurociencia del renacimiento', '11 Ritos de poder personal', 'El mapa del yo futuro', 'La ceremonia del nombre nuevo', 'El Gran Rito de Cierre Final']
          }].map((tomo, i) => <div key={i} className="group relative bg-[#1A0B2E] rounded-[2rem] border border-[#C9A84C]/10 overflow-hidden hover:border-[#C9A84C]/40 transition-all duration-500" data-id="element-116">
                <div className="absolute top-0 right-0 p-12 opacity-5 group-hover:opacity-10 transition-opacity" data-id="element-117">
                  <span className="text-[12rem] font-bold text-[#C9A84C] leading-none" data-id="element-118">
                    {tomo.n}
                  </span>
                </div>
                <div className="p-8 md:p-16 relative z-10" data-id="element-119">
                  <span className="text-[#C9A84C] font-bold text-sm tracking-[0.4em] mb-4 block" data-id="element-120">
                    TOMO {tomo.n}
                  </span>
                  <h3 className="text-3xl md:text-5xl font-bold mb-6 text-white" data-id="element-121">
                    {tomo.t}
                  </h3>
                  <p className="text-[#8900F2] font-bold mb-8 text-sm uppercase tracking-widest" data-id="element-122">
                    Contenido Exclusivo
                  </p>
                  <p className="text-white/60 mb-10 text-xl font-light leading-relaxed max-w-2xl" data-id="element-123">
                    {tomo.d}
                  </p>
                  <ul className="grid md:grid-cols-2 gap-y-4 gap-x-12" data-id="element-124">
                    {tomo.f.map((feature, j) => <li key={j} className="flex items-center gap-4 text-white/90 text-lg font-light" data-id="element-125">
                        <span className="text-[#8900F2]" data-id="element-126">✦</span> {feature}
                      </li>)}
                  </ul>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="py-12 md:py-32 bg-[#1A0B2E]" data-id="element-127">
        <div className="container mx-auto px-4" data-id="element-128">
          <SectionTitle subtitle="Valor total bonos: $18.000 → GRATIS con tu pack" data-id="element-129">
            🎁 3 BONOS ESTRATÉGICOS
          </SectionTitle>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-130">
            {[{
            n: '01',
            t: '33 CARTAS DE INTENCIÓN SAGRADA',
            d: 'Activa el campo simbólico correcto antes de cada rito. Una carta por cada uno de los 33 ritos.',
            v: '$6.000'
          }, {
            n: '02',
            t: 'PROTOCOLO DE EMERGENCIA EMOCIONAL',
            d: '3 ritos de acción rápida para crisis nocturnas y ataques de angustia. Para cuando la noche pesa más que el día.',
            v: '$6.000'
          }, {
            n: '03',
            t: 'LISTA DE SUMINISTROS SAGRADOS',
            d: 'Todo lo que necesitás para los 33 ritos, dónde conseguirlo en Argentina/LATAM y a qué precio. Sin gastar de más.',
            v: '$6.000'
          }].map((bono, i) => <div key={i} className="bg-[#0a0412] p-10 rounded-3xl border border-[#C9A84C]/10 relative overflow-hidden group hover:border-[#8900F2]/50 transition-all" data-id="element-131">
                <div className="absolute top-0 right-0 bg-[#8900F2] text-white text-[10px] font-bold px-6 py-2 rounded-bl-2xl uppercase tracking-widest" data-id="element-132">
                  Gratis
                </div>
                <span className="text-[#C9A84C] font-bold text-sm mb-4 block tracking-[0.3em]" data-id="element-133">
                  BONO {bono.n}
                </span>
                <h4 className="text-2xl font-bold mb-6 text-white leading-tight" data-id="element-134">
                  {bono.t}
                </h4>
                <p className="text-white/50 text-lg font-light mb-10 leading-relaxed" data-id="element-135">
                  {bono.d}
                </p>
                <p className="text-sm line-through text-red-400/50 font-bold" data-id="element-136">
                  {bono.v}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-8 md:py-32 bg-[#0a0412]" id="pricing" data-id="element-137">
        <div className="container mx-auto px-4" data-id="element-138">
          <SectionTitle light subtitle="Acceso inmediato a todo el material en formato PDF." data-id="element-139">
            LLEVATE TU PACK COMPLETO
          </SectionTitle>

          <div className="hidden md:flex justify-center mb-16" data-id="element-140">
            <div className="bg-[#1A0B2E] p-8 rounded-3xl border border-[#C9A84C]/30 shadow-[0_0_50px_rgba(201,168,76,0.1)]" data-id="element-141">
              <CountdownTimer data-id="element-142" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto" data-id="element-143">
            <div className="bg-[#1A0B2E] rounded-[2rem] md:rounded-[3rem] p-4 md:p-16 text-white shadow-2xl flex flex-col border-4 border-[#C9A84C] relative overflow-hidden" data-id="element-144">
              <div className="mb-2 md:mb-12 text-center mt-4 md:mt-0" data-id="element-145">
                <h3 className="text-xl md:text-4xl font-bold text-white mb-1 md:mb-4" data-id="element-146">
                  🌑 EL MÉTODO DE LOS 33 RITOS™
                </h3>
                <p className="text-white/40 text-[10px] md:text-sm uppercase tracking-widest" data-id="element-147">
                  4 Tomos + 3 Bonos
                </p>
              </div>

              <div className="mb-2 md:mb-12 text-center" data-id="element-148">
                <p className="text-white/30 line-through text-sm md:text-2xl mb-0 md:mb-2" data-id="element-149">
                  $48.000 ARS
                </p>
                <p className="text-3xl md:text-8xl font-bold text-[#C9A84C] drop-shadow-[0_0_30px_rgba(201,168,76,0.3)]" data-id="element-150">
                  $14.990{' '}
                  <span className="text-lg md:text-4xl font-medium" data-id="element-151">ARS</span>
                </p>
              </div>

              <ul className="space-y-1 md:space-y-5 mb-4 md:mb-16 flex-grow" data-id="element-152">
                {['Tomo I — El Oráculo del Inconsciente', 'Tomo II — Cortes Vinculares', 'Tomo III — Purga de Sombras', 'Tomo IV — Arquitectura del Renacimiento', 'Bono 1 — 33 Cartas de Intención', 'Bono 2 — Protocolo de Emergencia', 'Bono 3 — Lista de Suministros', '270+ páginas listas para trabajar', 'Acceso inmediato PDF'].map((item, i) => <li key={i} className="flex items-start gap-2 md:gap-4 text-white/90 text-[11px] md:text-lg font-light" data-id="element-153">
                    <CheckCircle2 size={14} className="text-[#C9A84C] shrink-0 mt-0.5 md:mt-0" data-id="element-154" />
                    <span data-id="element-155">{item}</span>
                  </li>)}
              </ul>

              <CTAButton className="w-full text-sm md:text-2xl py-3 md:py-8 mb-2 md:mb-8" data-id="element-156">
                → QUIERO MI MÉTODO AHORA
              </CTAButton>

              <div className="bg-red-500/10 text-red-400 p-2 md:p-4 rounded-xl text-center font-bold text-[10px] md:text-lg border border-red-500/20" data-id="element-157">
                ⚠️ Después sube a $22.990 ARS. No vuelve.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-12 md:py-32 bg-[#1A0B2E]" data-id="element-158">
        <div className="container mx-auto px-4" data-id="element-159">
          <SectionTitle subtitle="El próximo ciclo que no cierres puede costarte meses de tu vida." data-id="element-160">
            ⚖️ ¿CUÁNTO CUESTA NO TENERLO?
          </SectionTitle>

          <div className="max-w-4xl mx-auto space-y-6" data-id="element-161">
            {[{
            s: 'Pensamiento que no para',
            no: 'Bucle mental sin salida',
            yes: 'Tomo I: Rito del Primer Silencio'
          }, {
            s: 'Ex-pareja que no podés soltar',
            no: 'Bloqueo digital que no funciona',
            yes: 'Tomo II: 10 ritos de corte vincular'
          }, {
            s: 'Culpa que no te pertenece',
            no: 'Cargarla indefinidamente',
            yes: 'Tomo III: Rito de devolución del linaje'
          }, {
            s: 'Crisis a las 3 de la mañana',
            no: 'Sin herramientas, sola/o',
            yes: 'Bono 2: Protocolo de emergencia listo'
          }, {
            s: 'No saber cómo empezar',
            no: 'Parálisis e improvisación',
            yes: 'Bono 1: Carta de intención lista'
          }].map((row, i) => <div key={i} className="overflow-hidden rounded-3xl border border-[#C9A84C]/20 shadow-xl bg-[#2D0050]/20" data-id="element-162">
                <div className="p-6 bg-[#2D0050] border-b border-[#C9A84C]/10" data-id="element-163">
                  <h4 className="text-[#C9A84C] font-bold uppercase tracking-widest text-sm mb-1" data-id="element-164">
                    SITUACIÓN
                  </h4>
                  <p className="text-xl md:text-2xl font-bold text-white" data-id="element-165">
                    {row.s}
                  </p>
                </div>
                <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10" data-id="element-166">
                  <div className="p-6 bg-red-500/5" data-id="element-167">
                    <span className="text-red-400 font-bold text-xs uppercase tracking-widest block mb-2" data-id="element-168">
                      ❌ SIN EL MÉTODO
                    </span>
                    <p className="text-red-400/80 italic text-lg" data-id="element-169">{row.no}</p>
                  </div>
                  <div className="p-6 bg-[#00C48C]/5" data-id="element-170">
                    <span className="text-[#00C48C] font-bold text-xs uppercase tracking-widest block mb-2" data-id="element-171">
                      ✅ CON EL MÉTODO
                    </span>
                    <p className="text-[#00C48C] font-bold text-lg" data-id="element-172">
                      {row.yes}
                    </p>
                  </div>
                </div>
              </div>)}

            <div className="bg-[#2D0050] text-white p-8 md:p-10 text-center rounded-3xl border border-[#C9A84C]/20 shadow-2xl mt-8" data-id="element-173">
              <p className="text-xl md:text-2xl font-bold tracking-wide" data-id="element-174">
                "Tu proceso de cierre puede empezar hoy mismo." 🌑
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials 2 */}
      <section className="py-12 md:py-32 bg-[#0a0412]" data-id="element-175">
        <div className="container mx-auto px-4" data-id="element-176">
          <SectionTitle subtitle="Más personas que ya transformaron su dolor en liviandad." data-id="element-177">
            ❤️ TESTIMONIOS
          </SectionTitle>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-178">
            {[{
            n: 'Sofía M.',
            l: 'Mendoza · Coach ontológica',
            q: 'Hice el Tomo II un domingo a la tarde con velas y todo. Lloré, me reí y al final sentí algo que no sentía hace años: la liviandad.',
            i: 'SM'
          }, {
            n: 'Ricardo P.',
            l: 'Buenos Aires · Terapeuta floral',
            q: 'El Protocolo de Emergencia lo usé la primera semana. Crisis de madrugada. Funcionó en 5 minutos. No lo puedo creer todavía.',
            i: 'RP'
          }, {
            n: 'Andrea L.',
            l: 'Santa Fe · Docente universitaria',
            q: 'El Rito del Pacto con la Sombra fue el más difícil y el más liberador de todo el método. Sentí que me perdoné algo que cargaba hace 20 años.',
            i: 'AL'
          }, {
            n: 'Martín G.',
            l: 'Córdoba · Psicólogo clínico',
            q: 'Lo recomiendo a todos mis pacientes como complemento del proceso terapéutico. La base científica me dio la confianza para hacerlo.',
            i: 'MG'
          }].map((test, i) => <div key={i} className="bg-[#1A0B2E] p-10 rounded-3xl border border-[#C9A84C]/20 flex gap-8 items-start group hover:border-[#8900F2]/50 transition-all" data-id="element-179">
                <div className="w-16 h-16 bg-[#8900F2] rounded-full shrink-0 flex items-center justify-center font-bold text-white text-xl shadow-[0_0_20px_rgba(137,0,242,0.4)]" data-id="element-180">
                  {test.i}
                </div>
                <div data-id="element-181">
                  <div className="flex gap-1 mb-4 text-[#C9A84C]" data-id="element-182">
                    {[...Array(5)].map((_, j) => <Star key={j} size={16} fill="currentColor" data-id="element-183" />)}
                  </div>
                  <p className="text-white/80 italic mb-6 text-lg font-light leading-relaxed" data-id="element-184">
                    "{test.q}"
                  </p>
                  <p className="font-bold text-[#C9A84C] tracking-wide" data-id="element-185">
                    {test.n}
                  </p>
                  <p className="text-xs text-[#8900F2] uppercase tracking-[0.2em] font-bold mt-1" data-id="element-186">
                    {test.l}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-12 md:py-32 bg-[#1A0B2E]" data-id="element-187">
        <div className="container mx-auto px-4 text-center" data-id="element-188">
          <div className="max-w-4xl mx-auto bg-[#0a0412] p-12 md:p-24 rounded-[3rem] border-2 border-[#C9A84C] shadow-2xl relative overflow-hidden" data-id="element-189">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-[#C9A84C] text-[#1A0B2E] px-10 py-2 rounded-b-2xl font-bold text-xs tracking-widest uppercase" data-id="element-190">
              Garantía Total
            </div>
            <ShieldCheck size={80} className="text-[#C9A84C] mx-auto mb-10 drop-shadow-[0_0_20px_rgba(201,168,76,0.4)]" data-id="element-191" />
            <h2 className="text-4xl md:text-6xl font-bold mb-8 text-white" data-id="element-192">
              GARANTÍA 7 DÍAS SIN PREGUNTAS
            </h2>
            <p className="text-xl md:text-2xl text-white/70 mb-12 leading-relaxed font-light" data-id="element-193">
              Si abrís el pack y no es lo que esperabas: <br data-id="element-194" />
              <span className="text-4xl md:text-6xl font-bold text-[#C9A84C] block mt-6 drop-shadow-[0_0_20px_rgba(201,168,76,0.3)]" data-id="element-195">
                DEVOLUCIÓN DEL 100%
              </span>
              <span className="text-lg text-white/40 mt-4 block" data-id="element-196">
                sin formularios ni vueltas.
              </span>
            </p>
            <div className="flex flex-col items-center gap-2" data-id="element-197">
              <p className="text-xs text-[#C9A84C] uppercase tracking-[0.4em] font-bold" data-id="element-198">
                Tasa de devoluciones: menos del 1%
              </p>
              <p className="text-sm text-white/30 italic" data-id="element-199">
                El método habla por sí solo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-32 bg-[#0a0412]" data-id="element-200">
        <div className="container mx-auto px-4" data-id="element-201">
          <SectionTitle subtitle="Despejá tus dudas y empezá hoy mismo." data-id="element-202">
            ❓ LO QUE MÁS NOS PREGUNTAN
          </SectionTitle>
          <div className="max-w-4xl mx-auto space-y-6" data-id="element-203">
            {[{
            q: '¿Para quién es este método?',
            a: 'Para cualquier persona que esté atravesando o haya atravesado una ruptura, pérdida, traición o momento de quiebre y sienta que no puede soltar aunque lo entienda.'
          }, {
            q: '¿Necesito creer en lo espiritual para que funcione?',
            a: 'No. Cada rito tiene base científica documentada. Funciona también en personas completamente escépticas.'
          }, {
            q: '¿Cómo recibo el material?',
            a: 'Inmediatamente después del pago recibís un email con los links de descarga de los 7 archivos PDF.'
          }, {
            q: '¿Tiene garantía real?',
            a: 'Sí. 7 días de devolución completa, sin preguntas ni formularios.'
          }, {
            q: '¿Es lo mismo que un libro de autoayuda?',
            a: 'No. La diferencia es que acá se hace algo. No solo se lee. Cada rito tiene materiales, paso a paso y carta de integración. Es un sistema activo, no pasivo.'
          }, {
            q: '¿Puedo usarlo mientras hago terapia?',
            a: 'Sí. De hecho muchos terapeutas lo recomiendan a sus pacientes como complemento del proceso.'
          }].map((faq, i) => <details key={i} className="group bg-[#1A0B2E] rounded-3xl overflow-hidden border border-white/5 hover:border-[#C9A84C]/20 transition-all" data-id="element-204">
                <summary className="flex items-center justify-between p-8 cursor-pointer font-bold text-xl text-[#C9A84C] list-none" data-id="element-205">
                  {faq.q}
                  <ChevronDown size={24} className="text-[#8900F2] group-open:rotate-180 transition-transform" data-id="element-206" />
                </summary>
                <div className="px-8 pb-8 bg-[#1A0B2E] text-white/70 leading-relaxed text-lg font-light border-l-8 border-[#C9A84C]" data-id="element-207">
                  {faq.a}
                </div>
              </details>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-16 md:py-48 overflow-hidden text-center" data-id="element-208">
        <div className="absolute inset-0 z-0" data-id="element-209">
          <img src="https://images.unsplash.com/photo-1462331940025-496dfbfc7564?auto=format&fit=crop&q=80&w=2000" className="w-full h-full object-cover opacity-40" alt="Deep Space Background" data-id="element-210" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A0B2E] via-[#1A0B2E]/80 to-transparent" data-id="element-211"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10" data-id="element-212">
          <SectionTitle light subtitle="Tu proceso de cierre puede empezar hoy mismo. 🌑" data-id="element-213">
            ⏰ EL PRECIO CIERRA EN:
          </SectionTitle>

          <div className="flex justify-center mb-16" data-id="element-214">
            <div className="bg-white/5 backdrop-blur-3xl p-10 rounded-[2.5rem] border border-white/10 shadow-[0_0_100px_rgba(137,0,242,0.2)]" data-id="element-215">
              <CountdownTimer data-id="element-216" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-20" data-id="element-217">
            <div className="bg-[#1A0B2E] rounded-[3.5rem] p-10 md:p-20 text-white shadow-2xl flex flex-col border-4 border-[#C9A84C] relative" data-id="element-218">
              <h3 className="text-3xl md:text-5xl font-bold mb-8" data-id="element-219">
                🌑 EL MÉTODO DE LOS 33 RITOS™
              </h3>
              <p className="text-white/30 line-through text-3xl mb-2" data-id="element-220">
                $48.000 ARS
              </p>
              <p className="text-7xl md:text-9xl font-bold text-[#C9A84C] mb-12 drop-shadow-[0_0_40px_rgba(201,168,76,0.4)]" data-id="element-221">
                $14.990 ARS
              </p>
              <CTAButton className="w-full text-3xl py-10 shadow-[0_0_50px_rgba(137,0,242,0.5)]" data-id="element-222">
                → QUIERO EL PACK COMPLETO AHORA
              </CTAButton>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-6 md:gap-12" data-id="element-223">
            <div className="flex items-center gap-3 bg-white/5 px-8 py-4 rounded-full text-white font-bold text-sm uppercase tracking-[0.2em] border border-white/10" data-id="element-224">
              <ShieldCheck size={20} className="text-[#00C48C]" data-id="element-225" /> Pago 100%
              Seguro
            </div>
            <div className="flex items-center gap-3 bg-white/5 px-8 py-4 rounded-full text-white font-bold text-sm uppercase tracking-[0.2em] border border-white/10" data-id="element-226">
              <Star size={20} className="text-[#C9A84C]" data-id="element-227" /> Garantía de
              Satisfacción
            </div>
            <div className="flex items-center gap-3 bg-white/5 px-8 py-4 rounded-full text-white font-bold text-sm uppercase tracking-[0.2em] border border-white/10" data-id="element-228">
              <Zap size={20} className="text-[#8900F2]" data-id="element-229" /> Acceso Instantáneo
            </div>
          </div>

          <div className="mt-16 bg-red-500/20 text-red-400 p-4 rounded-2xl inline-block font-bold border border-red-500/30 tracking-widest uppercase text-sm" data-id="element-230">
            ⚠️ Después sube a $22.990 ARS. No vuelve.
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 md:py-24 bg-[#0a0412] text-white/40 border-t border-white/5" data-id="element-231">
        <div className="container mx-auto px-4 text-center" data-id="element-232">
          <p className="font-bold text-white text-3xl mb-6 font-display tracking-widest" data-id="element-233">
            🌑 EL MÉTODO DE LOS 33 RITOS™
          </p>
          <p className="text-lg mb-12 max-w-3xl mx-auto leading-relaxed font-light" data-id="element-234">
            El sistema de Psicología Simbólica N°1 en español para cerrar
            ciclos, cortar vínculos y renacer desde adentro.
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-8 md:gap-16 mb-16 text-sm tracking-widest uppercase font-bold" data-id="element-235">
            <a href="mailto:contacto@edicionesumbral.com" className="text-[#C9A84C] hover:text-white transition-colors flex items-center justify-center gap-3" data-id="element-236">
              <MessageSquare size={18} data-id="element-237" /> contacto@edicionesumbral.com
            </a>
            <div className="flex items-center justify-center gap-3 text-[#8900F2]" data-id="element-238">
              <Star size={18} data-id="element-239" /> @edicionesumbral
            </div>
          </div>
          <div className="w-full h-px bg-white/5 mb-12" data-id="element-240"></div>
          <p className="text-xs tracking-[0.3em] uppercase mb-6" data-id="element-241">
            © Ediciones Umbral™ 2026 — Todos los derechos reservados.
          </p>
          <p className="text-[#C9A84C] text-xl md:text-2xl font-bold italic" data-id="element-242">
            "Lo que se cierra con conciencia, libera para siempre." 🌑
          </p>
        </div>
      </footer>
    </div>;
}